import { EditorProps as TipTapEditorProps } from "@tiptap/pm/view";
import { Editor, EditorContext, useEditor } from "@tiptap/react";
import * as React from "react";
import { useCollab } from "../contexts/collab-context";
import type { User } from "../contexts/user-context";
import { extensions } from "./extensions";
import "./tiptap-node/blockquote-node/blockquote-node.scss";
import "./tiptap-node/code-block-node/code-block-node.scss";
import "./tiptap-node/heading-node/heading-node.scss";
import "./tiptap-node/horizontal-rule-node/horizontal-rule-node.scss";
import "./tiptap-node/image-node/image-node.scss";
import "./tiptap-node/list-node/list-node.scss";
import "./tiptap-node/paragraph-node/paragraph-node.scss";
import { EditorProps } from "./types";

type EditorProviderProps = EditorProps & {
  user: User;
  children: React.ReactNode;
  editorProps: TipTapEditorProps;
  placeholderEmptyNodeClass?: string;
};
/**
 * Loading spinner component shown while connecting to the notion server
 */
const LoadingSpinner: React.FC<{ text?: string }> = ({
  text = "Connecting...",
}) => (
  <div className="spinner-container">
    <div className="spinner-content">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <div className="spinner-loading-text">{text}</div>
    </div>
  </div>
);

export function EditorProvider(props: EditorProviderProps) {
  const { placeholder = "Start writing...", user, onUpdate } = props;

  const { provider, localProvider, ydoc, isSynced, error, isSyncedLocal } =
    useCollab();

  const isLoading =
    props.connectToRemoteSync === false
      ? false
      : (!isSynced && // remote data is not synced yet
          localProvider &&
          localProvider.doc.store.clients.size === 0) || // local client is empty
        !isSyncedLocal; // local document is not synced yet

  const editorRef = React.useRef<Editor | null>(null);

  const editor = useEditor(
    {
      immediatelyRender: false,
      shouldRerenderOnTransaction: false,
      editorProps: props.editorProps,
      autofocus: true,
      extensions: extensions({
        provider,
        ydoc,
        user: { name: user.firstName ?? "Anonymous User", color: user.color },
        placeholder,
        placeholderEmptyNodeClass: props.placeholderEmptyNodeClass,
      }),
      onUpdate: onUpdate,
      onContentError({ editor, disableCollaboration }) {
        // Removes the collaboration extension.
        disableCollaboration();

        // Since the content is invalid, we don't want to emit an update
        // Preventing synchronization with other editors or to a server
        const emitUpdate = false;

        // Disable the editor to prevent further user input
        editor.setEditable(false, emitUpdate);

        // Maybe show a notification to the user that they need to refresh the app
        // TODO
      },
    },
    [provider],
  );

  React.useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  React.useEffect(() => {
    if (isLoading === false && editor) {
      editor.commands.focus("start");
    }
  }, [isLoading]);

  if (error) {
    return (
      <div className="text-destructive p-8">
        <p>Error connecting to the editor: {error.message}</p>
      </div>
    );
  }

  return (
    <EditorContext.Provider value={{ editor }}>
      {props.children}
    </EditorContext.Provider>
  );
}

export function useEditorContext() {
  const editor = React.useContext(EditorContext);

  if (!editor) {
    throw new Error("Editor not found");
  }

  return editor;
}
